<template>
    <div class="items-container">
        <div v-if="invalidRowsCount > 0"
             class="items-error-count"
        >
            <svg class="items-error-count-icon">
                <use xlink:href="#exclamation-heavy" />
            </svg>
            {{ $t('items.bulk.error.invalidSkusToBeFixed', { count: invalidRowsCount }) }}
        </div>
        <base-table v-if="parent.items.length > 0"
                    :ref="`table-${parent.id}`"
                    :columns="columns"
                    :rows="parent.items"
                    :actions="itemsActionsComputed"
                    row-uniq-id="idx"
        >
            <template #column-default-tpl="{ row, col, colIdx }">
                <field-wrapper :for-id="`i_${colIdx}-${row.idx}-input`"
                               :hide-label="true"
                               :error="getColError(row, col) || getServerSideError(row, col)"
                >
                    <div
                        class="cell-container"
                        :class="{ 'cell-custom-error' : row.errorMessage && row.errorMessage.length && col.field == 'sku' }"
                    >
                        <span v-if="col.field === 'isSerialSku' && isVisibleRecordSerialNumber && row.isRecordSerialNumber">
                            <button-tooltip
                                v-tooltip.bottom="{
                                    content: $t('items.labels.isRecordSerialNumberWillCaptureToolTip'),
                                    delay: { show: 500, hide: 300 },
                                }"
                                :display-star="true"
                            />
                        </span>
                        <span v-if="col.field === 'expirationDate'">{{ row[col.field] | date('short') | valueOrDash }}</span>
                        <span v-else-if="col.field != 'quantity'">{{ row[col.field] }}</span>
                        <span v-else>
                            {{ row[col.field] | valueOrDash }}
                            <span v-if="!hideAvailableQty"> / </span>
                            <span
                                v-if="!hideAvailableQty"
                                :class="{ 'no-items-error' : !getAvailableCount(row) }"
                            >
                                {{ getAvailableCount(row) | valueOrDash }}
                            </span>
                        </span>
                        <svg v-if="getColError(row, col) != null && getColError(row, col).length > 0
                                 || getServerSideError(row, col) != null"
                             class="item-error-icon"
                        >
                            <use xlink:href="#exclamation-heavy" />
                        </svg>
                    </div>
                </field-wrapper>
            </template>
        </base-table>
    </div>
</template>

<script>
import '@/assets/icons/exclamation-heavy.svg';
import { $t } from '@/translations';
import BaseTable from '@/components/table/BaseTable';
import FieldWrapper from '@/components/form/FieldWrapper';
import validationRules from './validation-rules';
import ButtonTooltip from '@/components/buttons/ButtonTooltip';

export default {
    name: 'BulkItemsTable',
    components: {
        BaseTable,
        FieldWrapper,
        ButtonTooltip,
    },
    props: {
        id: {
            type: String,
            required: true,
        },
        itemParent: {
            type: Object,
            required: true,
        },
        validity: {
            type: Array,
            required: false,
            default() {
                return [];
            },
        },
        invalidItemsCount: {
            type: Number,
            required: false,
            default: 0,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        hideAvailableQty: {
            type: Boolean,
            default: false,
        },
        selectedWarehouse: {
            type: String,
            default: null,
        },
        isVisibleRecordSerialNumber: {
            type: Boolean,
            default: false,
        },
        isDisplayLotDetail: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            itemsActions: [
                {
                    title: $t('general.edit'),
                    icon: 'pencil',
                    size: '20px',
                    handler: (event, row) => this.openItemForEdit(row),
                },
                {
                    title: $t('general.delete'),
                    icon: 'trash',
                    size: '20px',
                    handler: (event, row) => this.delete(row),
                },
            ],
        };
    },
    computed: {
        columns() {
            const baseColumns = [
                {
                    name: '',
                    field: 'isSerialSku',
                    type: 'string',
                    minWidth: '1%',
                    rules: '',
                },
                {
                    name: this.$t('items.bulk.table.sku'),
                    field: 'sku',
                    type: 'string',
                    rules: validationRules.sku,
                    minWidth: '29%',
                },
                {
                    name: this.$t('items.bulk.table.description'),
                    field: 'description',
                    type: 'string',
                    minWidth: '30%',
                },
            ];

            // Conditionally add lot/expiration columns
            if (this.isDisplayLotDetail) {
                baseColumns.push(
                    {
                        name: this.$t('items.bulk.table.lotNumber'),
                        field: 'lotNumber',
                        type: 'string',
                        minWidth: '10%',
                    },
                    {
                        name: this.$t('items.bulk.table.expirationDate'),
                        field: 'expirationDate',
                        type: 'string',
                        minWidth: '10%',
                    },
                );
            }

            baseColumns.push({
                name: this.$t('items.bulk.table.quantity'),
                field: 'quantity',
                type: 'number',
                rules: validationRules.quantity,
                minWidth: '20%',
                width: '20%',
                hint: this.hideAvailableQty ? '' : this.$t('items.bulk.table.tooltip'),
            });

            return baseColumns;
        },
        fieldError() {
            const cols = this.columns.map(col => col.field);
            return this.validity.reduce((acc, row) => {
                cols.forEach((col) => {
                    const key = `${row.idx}_${col}`;
                    acc[key] = (row[col] && row[col].errors) || [];
                });
                return acc;
            }, {});
        },
        parent() {
            return this.itemParent;
        },
        invalidRowsCount() {
            return this.invalidItemsCount;
        },
        itemsActionsComputed() {
            return this.disabled ? [] : this.itemsActions;
        },
        getColError() {
            return (row, col) => this.fieldError[`${row.idx}_${col.field}`];
        },
        getServerSideError() {
            /* eslint-disable no-else-return */
            return (row, col) => {
                if (row.errorMessage && row.errorMessage.length > 0 && row.errorMessage.some) {
                    if (col.field === 'sku' && row.errorMessage.some(x => x.errorFieldName === 'ItemId')) {
                        return row.errorMessage.filter(x => x.errorFieldName === 'ItemId').map(x => x.errorDetails);
                    } else if (col.field === 'quantity' && row.errorMessage.some(x => x.errorFieldName === 'Quantity')) {
                        return row.errorMessage.filter(x => x.errorFieldName === 'Quantity').map(x => x.errorDetails);
                    }
                }

                return null;
            };
        },
        getAvailableCount() {
            return (item) => {
                if (this.selectedWarehouse == null || item.availableInventories == null) {
                    return item.availableQty;
                }

                if (item.isLotTracked && item.lotNumber !== null) {
                    if (item.isExpirationTracked === false) {
                        return item.availableLotQty;
                    }

                    if (item.isExpirationTracked && item.expirationDate !== null) {
                        return item.availableLotQty;
                    } else {
                        return item.availableQty;
                    }
                }

                const itemWarehouse = item.availableInventories.find(i => i.warehouseName.toUpperCase() === this.selectedWarehouse.toUpperCase());
                if (itemWarehouse) {
                    return itemWarehouse.availableInventoryQuantity;
                }

                return 0;
            };
        },
    },
    methods: {
        async delete(row) {
            this.$emit('delete-item', row);
        },
        openItemForEdit(item) {
            this.$emit('open-edit-item', item, this.itemParent);
        },
    },
};
</script>

<style lang="scss" scoped>
.items-container {
    margin-top: 20px;

    .items-error-count {
        position: relative;
        padding: 0 0 10px 24px;
        color: $color-error;
        font-size: $font-size-base;

        &-icon {
            position: absolute;
            top: 1px;
            left: 0;
            width: 20px;
            height: 20px;
        }
    }

    .cell-container {
        position: relative;
        width: fit-content;
        min-height: 20px;

        .item-error-icon {
            position: absolute;
            top: calc(50% - 10px);
            right: 0;
            width: 20px;
            height: 20px;
            color: $color-error;
        }

        &.cell-custom-error {
            padding-right: 24px;

            .item-error-icon {
                top: calc(50% - 12px);
            }
        }

        .no-items-error {
            color: $color-error;
        }
    }

    .field--invalid .cell-container {
        padding-right: 25px;
    }
}
</style>
